/**
 * v1.3 project references (CONTRACT 18.1.1, 18.11 "Refs"). A project is reached by its code (`PJ0037-26`) when it has one,
 * else by its numeric id (`85`): every project link and every project cache key uses `projectRef(p) = p.code ?? String(p.id)`.
 * Pure: no React, no query client.
 */

/** A project code as the API accepts it in a ref (case-insensitive here; the canonical form is upper-case). */
const PROJECT_CODE_RE = /^PJ\d{4}-\d{2}$/i;

export interface ProjectRefSource {
  id: number;
  code: string | null;
}

/** True for a project code (`PJ0037-26`, any case, surrounding blanks ignored); false for an id or anything else. */
export function isProjectCode(ref: string): boolean {
  return PROJECT_CODE_RE.test(ref.trim());
}

/** Canonical form of a URL / cache ref: codes upper-cased, ids (and anything else) only trimmed. */
export function normalizeProjectRef(ref: string): string {
  const t = ref.trim();
  return isProjectCode(t) ? t.toUpperCase() : t;
}

/** The URL ref of a project: its code, or its numeric id while it has none. */
export function projectRef(p: ProjectRefSource): string {
  return p.code ?? String(p.id);
}

/** `/pm/projects/PJ0037-26` or `/pm/projects/85`. */
export function projectHref(p: ProjectRefSource): string {
  return `/pm/projects/${encodeURIComponent(projectRef(p))}`;
}

/** 'PJ0037-26 Name', or just the name for a code-less project. */
export function projectLabel(p: { code: string | null; name: string }): string {
  return p.code ? `${p.code} ${p.name}` : p.name;
}

/**
 * Deep link to one task on its project page (`#task-<id>`; the project page scrolls to and highlights it). `person`
 * (e.g. 'me') opens the page with its "Tasks for" filter set (`?person=`).
 */
export function taskHref(p: ProjectRefSource, taskId: number, person?: string): string {
  return `${projectHref(p)}${person ? `?person=${encodeURIComponent(person)}` : ''}#task-${taskId}`;
}

/** Deep link to one deadline on its project page (`#deadline-<id>`). */
export function deadlineRefHref(p: ProjectRefSource, deadlineId: number): string {
  return `${projectHref(p)}#deadline-${deadlineId}`;
}
